import React, { useEffect, useRef, useState } from "react";
import { purchasingApi } from "../Purchasing/api";
import { brief, manualQuote, snapshot, TYPES } from "./webQuoteHelpers.mjs";
import "./webQuotes.css";

const INITIAL = {
  businessUnit: "empaque",
  type: "mailer",
  length: "",
  width: "",
  height: "",
  dimensionUnit: "in",
  material: "",
  printing: "outside",
  quantity: "",
  designs: 1,
  destinationCountry: "US",
  destinationPostalCode: "",
  neededBy: "",
  notes: "",
};
const blank = () => ({
  subtotal: "",
  currency: "USD",
  setup: "",
  shipping: "",
  tax: "",
  confirmed: false,
  notes: "",
  saved: null,
});
const money = (value, currency) =>
  value == null
    ? "Sin confirmar"
    : `${currency} ${Number(value).toLocaleString("es", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      })}`;
const date = (value) =>
  value ? new Date(value).toLocaleString("es") : "Sin fecha";
const STATUS = {
  manual: "Consulta manual",
  quoted: "Precio API · revisar",
  unavailable: "No disponible",
  unsupported: "Requiere otra configuración",
};

export default function WebQuotes({ api = purchasingApi, onDownload, initialInput, source }) {
  const [providers, setProviders] = useState([]),
    [input, setInput] = useState(() => initialInput ? {...INITIAL, ...initialInput} : INITIAL),
    [selected, setSelected] = useState([]),
    [data, setData] = useState(null),
    [manual, setManual] = useState({}),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true);
  const lock = useRef(false),
    sequence = useRef(0),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    sequence.current++;
    lock.current = false;
    setBusy(false);
    setLoading(true);
    setData(null);
    setManual({});
    let active = true;
    api("web-quotes/providers?businessUnit=empaque")
      .then((value) => {
        if (!Array.isArray(value))
          throw Error("No se pudo cargar la lista de proveedores.");
        if (active) {
          setProviders(value);
          setSelected(
            value
              .filter((p) => p.country === "US" && p.types.includes(initialInput?.type || "mailer"))
              .map((p) => p.id)
          );
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      mounted.current = false;
    };
  }, [api, initialInput]);
  function change(key, value) {
    setInput((previous) => ({ ...previous, [key]: value }));
    setData(null);
    setManual({});
    setError("");
    if (key === "type")
      setSelected(
        providers
          .filter((p) => p.country === "US" && p.types.includes(value))
          .map((p) => p.id)
      );
  }
  function toggle(id) {
    setSelected((previous) =>
      previous.includes(id)
        ? previous.filter((x) => x !== id)
        : [...previous, id]
    );
    setData(null);
    setManual({});
  }
  async function compare(event, forceRefresh = false) {
    event?.preventDefault();
    if (lock.current) return;
    lock.current = true;
    const current = ++sequence.current;
    setBusy(true);
    setError("");
    setData(null);
    setManual({});
    try {
      const body = {
        ...input,
        length: Number(input.length),
        width: Number(input.width),
        height: Number(input.height),
        quantity: Number(input.quantity),
        designs: Number(input.designs),
        supplierIds: selected,
        forceRefresh,
      };
      const result = await api("web-quotes/compare", "POST", body);
      if (!result || !Array.isArray(result.results) || !result.input)
        throw Error("Respuesta de comparación inválida.");
      if (mounted.current && sequence.current === current) setData(result);
    } catch (e) {
      if (mounted.current && sequence.current === current) setError(e.message);
    } finally {
      if (sequence.current === current) {
        lock.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  }
  function editManual(id, key, value) {
    setManual((previous) => ({
      ...previous,
      [id]: { ...blank(), ...previous[id], [key]: value, saved: null },
    }));
  }
  function record(id) {
    try {
      const draft = manual[id] || blank();
      const saved = manualQuote(draft, data.input.quantity);
      setManual((previous) => ({ ...previous, [id]: { ...draft, saved } }));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  function download() {
    const value = {...snapshot(data, manual), ...(source ? {source} : {})};
    if (onDownload) {
      onDownload(value);
      return;
    }
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(value, null, 2)], { type: "application/json" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "comparacion-proveedores.json";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const available = providers.filter((p) => p.types.includes(input.type));
  return (
    <section className="web-quotes" aria-labelledby="web-quotes-title">
      <header className="web-quotes__heading">
        <div>
          <h2 id="web-quotes-title">Cotizaciones web</h2>
          <p>
            Consulta precios para una misma especificación. Primer piloto: cajas
            y mailers.
          </p>
        </div>
        <span className="web-quotes__badge">
          Interno · sin pedidos automáticos
        </span>
      </header>
      <p className="web-quotes__notice">
        Los precios quedan en esta sesión y en memoria del servidor hasta 10
        minutos. No se guardan en la base de datos. Al salir se pierde la
        comparación; puedes descargarla explícitamente.
      </p>
      {error && (
        <p role="alert" className="web-quotes__error">
          {error}
        </p>
      )}
      {loading && <p role="status">Cargando proveedores…</p>}
      <form onSubmit={compare}>
        <fieldset disabled={busy || loading}>
          <legend>1. Define la especificación</legend>
          <div className="web-quotes__fields">
            <label>
              Formato
              <select
                value={input.type}
                onChange={(e) => change("type", e.target.value)}
              >
                {TYPES.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Unidad
              <select
                required
                value={input.dimensionUnit}
                onChange={(e) => change("dimensionUnit", e.target.value)}
              >
                <option value="">Por confirmar</option>
                <option value="in">Pulgadas</option>
                <option value="mm">Milímetros</option>
              </select>
            </label>
            {[
              ["length", "Largo interior"],
              ["width", "Ancho interior"],
              ["height", "Profundidad interior"],
            ].map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  required
                  type="number"
                  min="0.001"
                  max="10000"
                  step="any"
                  value={input[key]}
                  onChange={(e) => change(key, e.target.value)}
                />
              </label>
            ))}
            <label>
              Material y resistencia
              <input
                required
                maxLength="200"
                placeholder="Material, calibre o tipo de onda"
                value={input.material}
                onChange={(e) => change("material", e.target.value)}
              />
            </label>
            <label>
              Impresión
              <select
                required
                value={input.printing}
                onChange={(e) => change("printing", e.target.value)}
              >
                <option value="outside">Exterior</option>
                <option value="inside">Interior</option>
                <option value="both">Exterior e interior</option>
                <option value="">Por confirmar</option>
                <option value="none">Sin impresión</option>
              </select>
            </label>
            <label>
              Cantidad
              <input
                required
                type="number"
                min="1"
                max="1000000"
                step="1"
                value={input.quantity}
                onChange={(e) => change("quantity", e.target.value)}
              />
            </label>
            <label>
              Diseños (SKUs)
              <input
                required
                type="number"
                min="1"
                max="100"
                step="1"
                value={input.designs}
                onChange={(e) => change("designs", e.target.value)}
              />
            </label>
            <label>
              País de entrega (ISO)
              <input
                required
                pattern="[A-Za-z]{2}"
                maxLength="2"
                placeholder="US"
                value={input.destinationCountry}
                onChange={(e) =>
                  change("destinationCountry", e.target.value.toUpperCase())
                }
              />
            </label>
            <label>
              Código postal
              <input
                maxLength="20"
                value={input.destinationPostalCode}
                onChange={(e) =>
                  change("destinationPostalCode", e.target.value)
                }
              />
            </label>
            <label>
              Fecha requerida
              <input
                type="date"
                value={input.neededBy}
                onChange={(e) => change("neededBy", e.target.value)}
              />
            </label>
            <label className="web-quotes__wide">
              Acabados, insertos y requisitos adicionales
              <textarea
                maxLength="1000"
                value={input.notes}
                onChange={(e) => change("notes", e.target.value)}
              />
            </label>
          </div>
        </fieldset>
        <fieldset disabled={busy || loading}>
          <legend>2. Selecciona proveedores</legend>
          <div className="web-quotes__providers">
            {available.map((p) => (
              <label key={p.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
                <span>
                  {p.name}
                  <small>
                    {p.country} ·{" "}
                    {p.mode === "api"
                      ? "API pública, solo Argentina"
                      : "Cotizador manual"}
                  </small>
                </span>
              </label>
            ))}
          </div>
          <p>
            Los proveedores de EE. UU. requieren consulta manual hasta verificar
            acceso automatizado. No se usa el precio base del catálogo como
            cotización.
          </p>
          {input.type === "shipping_box" && (
            <p>
              Quilmes: Argentina, sin impresión, material «kraft c 4 mm», un
              diseño. El precio requiere revisión del troquel y del flete.
            </p>
          )}
        </fieldset>
        <div className="web-quotes__actions">
          <button
            className="p-button"
            disabled={busy || loading || !selected.length}
            type="submit"
          >
            {busy ? "Consultando…" : "Consultar proveedores"}
          </button>
          {data && (
            <button
              className="p-button p-button-outlined"
              type="button"
              disabled={busy}
              onClick={() => compare(null, true)}
            >
              Actualizar consulta
            </button>
          )}
        </div>
      </form>
      {busy && (
        <p role="status" aria-live="polite">
          Consultando proveedores compatibles. No se enviarán pedidos ni
          mensajes.
        </p>
      )}
      {data && (
        <div className="web-quotes__results" aria-live="polite">
          <header className="web-quotes__heading">
            <div>
              <h3>3. Revisa y compara</h3>
              <p>
                {date(data.generatedAt)}
                {data.cacheHit ? " · caché temporal" : ""}. No se comparan
                monedas distintas.
              </p>
            </div>
            <button className="p-button p-button-outlined" onClick={download}>
              Descargar comparación
            </button>
          </header>
          <details>
            <summary>Especificación para copiar al cotizador</summary>
            <pre className="web-quotes__brief">{brief(data.input)}</pre>
          </details>
          {data.results.map((result) => {
            const draft = manual[result.id] || blank(),
              saved = draft.saved,
              q = result.quote;
            return (
              <article key={result.id} className="web-quotes__result">
                <header>
                  <h4>{result.name}</h4>
                  <span className="web-quotes__badge">
                    {STATUS[result.status] || "Revisar"}
                  </span>
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir cotizador ↗
                  </a>
                </header>
                {result.reason && <p>{result.reason}</p>}
                {q && (
                  <>
                    <dl className="web-quotes__facts">
                      <div>
                        <dt>Total sin impuestos</dt>
                        <dd>{money(q.totalExTax, q.currency)}</dd>
                      </div>
                      <div>
                        <dt>Precio por unidad</dt>
                        <dd>{money(q.unitPrice, q.currency)}</dd>
                      </div>
                      <div>
                        <dt>Total con IVA</dt>
                        <dd>{money(q.totalWithTax, q.currency)}</dd>
                      </div>
                      <div>
                        <dt>Flete</dt>
                        <dd>Sin confirmar</dd>
                      </div>
                    </dl>
                    <p>
                      Vigencia informada: {q.validUntil}. {q.warnings.join(" ")}
                    </p>
                  </>
                )}
                {result.status === "manual" && (
                  <>
                    <div className="web-quotes__fields web-quotes__manual">
                      <label>
                        Total de productos sin cargos ni impuestos
                        <input
                          aria-label={`Total ${result.name}`}
                          type="number"
                          min="0.0001"
                          step="any"
                          value={draft.subtotal}
                          onChange={(e) =>
                            editManual(result.id, "subtotal", e.target.value)
                          }
                        />
                      </label>
                      <label>
                        Moneda
                        <select
                          aria-label={`Moneda ${result.name}`}
                          value={draft.currency}
                          onChange={(e) =>
                            editManual(result.id, "currency", e.target.value)
                          }
                        >
                          {["USD", "CAD", "ARS", "MXN", "EUR"].map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Preparación y otros cargos · vacío si no se conocen
                        <input
                          aria-label={`Preparación ${result.name}`}
                          type="number"
                          min="0"
                          step="any"
                          value={draft.setup}
                          onChange={(e) =>
                            editManual(result.id, "setup", e.target.value)
                          }
                        />
                      </label>
                      <label>
                        Flete · vacío si no se conoce
                        <input
                          aria-label={`Flete ${result.name}`}
                          type="number"
                          min="0"
                          step="any"
                          value={draft.shipping}
                          onChange={(e) =>
                            editManual(result.id, "shipping", e.target.value)
                          }
                        />
                      </label>
                      <label>
                        Impuestos · vacío si no se conocen
                        <input
                          aria-label={`Impuestos ${result.name}`}
                          type="number"
                          min="0"
                          step="any"
                          value={draft.tax}
                          onChange={(e) =>
                            editManual(result.id, "tax", e.target.value)
                          }
                        />
                      </label>
                      <label className="web-quotes__wide">
                        Diferencias o condiciones
                        <input
                          aria-label={`Condiciones ${result.name}`}
                          maxLength="1000"
                          value={draft.notes}
                          onChange={(e) =>
                            editManual(result.id, "notes", e.target.value)
                          }
                        />
                      </label>
                    </div>
                    <label className="web-quotes__check">
                      <input
                        type="checkbox"
                        checked={draft.confirmed}
                        onChange={(e) =>
                          editManual(result.id, "confirmed", e.target.checked)
                        }
                      />
                      Revisé que medidas, estructura, material, impresión,
                      cantidad y diseños coinciden.
                    </label>
                    <button
                      type="button"
                      className="p-button p-button-outlined"
                      onClick={() => record(result.id)}
                    >
                      Registrar en esta sesión
                    </button>
                    {saved && (
                      <p role="status">
                        Registrado {date(saved.observedAt)}. Total informado:{" "}
                        {money(saved.subtotal, saved.currency)}. Costo
                        entregado: {money(saved.landedTotal, saved.currency)}.{" "}
                        {saved.match === "needs_review"
                          ? "Compatibilidad pendiente."
                          : "Compatibilidad confirmada por el usuario; falta aprobar el troquel."}
                      </p>
                    )}
                  </>
                )}
              </article>
            );
          })}
          <p className="web-quotes__notice">
            Confirma costo entregado, plazo y troquel del proveedor antes de
            emitir una cotización firme. Los resultados no crean compras, RFQs
            ni registros de precios.
          </p>
        </div>
      )}
    </section>
  );
}
